import type { PlaceMatch } from '@chukua/common';
import { AutoComplete, Input } from 'antd';
import { Search } from 'lucide-react';
import type { Map as MapLibreMap } from 'maplibre-gl';
import { SEARCH_INPUT_PROPS } from '../../../../../shared/policies/search-input-props';
import { placeSearchEmptyMessage, placeSearchOptions, placeSearchPick } from '../../../public/place-search-options';
import { usePlaceSearch } from '../../../public/use-place-search';

interface ZonePlaceSearchProps {
  map: MapLibreMap | null;
  onPickPlace: (place: PlaceMatch) => void;
  /** An existing zone matched what was typed: open it rather than make a duplicate. */
  onPickZone: (zoneId: string) => void;
}

/** Starting a new zone the natural way: by typing the business, site or town it is. */
export function ZonePlaceSearch({ map, onPickPlace, onPickZone }: ZonePlaceSearchProps) {
  const { text, setText, state } = usePlaceSearch(map);

  function select(value: string) {
    const pick = placeSearchPick(value, state);
    setText('');
    if (pick?.type === 'zone') onPickZone(pick.zoneId);
    if (pick?.type === 'place') onPickPlace(pick.place);
  }

  return (
    <AutoComplete
      className="w-full"
      value={text}
      options={placeSearchOptions(state)}
      onSearch={setText}
      onSelect={select}
      notFoundContent={placeSearchEmptyMessage(state, 'Draw the zone on the map instead.')}
      popupMatchSelectWidth
    >
      <Input
        {...SEARCH_INPUT_PROPS}
        aria-label="Find a place"
        prefix={<Search className="size-4 text-muted" />}
        placeholder="Business, site or town, e.g. Bidco Thika"
        autoFocus
      />
    </AutoComplete>
  );
}
